import React, { FC, useEffect, useRef, useState } from 'react';
import { CustomButton, CustomInput, CustomModal } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import Loader from 'components/loader/Loader';
import { TeamProfileAddress } from 'containers/team-profile/teamProfile.model';
import { FormCloseIcon } from 'assets/icons';
import { REGEX } from 'constants/common';
import { ParsedAddress } from 'types/baseTypes';
import debounce from 'lodash/debounce';
import { createPlacesAutocompleteClients, googleAdressParser, whenGooglePlacesReady } from 'utils/maps';
import { useValidateAddressMutation } from 'containers/customers/api';
import { useGetStatesListQuery } from 'containers/team-profile/api';
import { useAppSelector } from 'hooks';
import { selectStates } from 'containers/customers/customersSlice';

interface CreateTeamAddressModalProps {
  open: boolean;
  onClose: () => void;
  updateAddress: (address: TeamProfileAddress) => void;
  initialAddress: TeamProfileAddress;
  pageState: 'ADD' | 'EDIT';
}

enum AddressFields {
  street = 'street',
  apartment = 'apartment',
  city = 'city',
  state = 'state',
  zipCode = 'zipCode'
}

const errorsInitialState = {
  state: '',
  zipCode: '',
  city: '',
  street: '',
  apartment: ''
};

const CreateTeamAddressModal: FC<CreateTeamAddressModalProps> = ({
  open,
  onClose,
  updateAddress,
  initialAddress,
  pageState
}) => {
  const [addressString, setAddressString] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [fullAddress, setFullAddress] = useState<ParsedAddress>(initialAddress);
  const [suggestedAddress, setSuggestedAddress] = useState<ParsedAddress>(initialAddress);
  const [errors, setErrors] = useState<Record<AddressFields, string>>(errorsInitialState);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const showExpanded = fullAddress?.city || fullAddress?.state || fullAddress?.zipCode;

  const autoCompleteRef = useRef(null);
  const sessionToken = useRef(null);
  const placesService = useRef(null);
  const suggestionsCache = useRef({});
  const latestAddressQueryRef = useRef('');

  useGetStatesListQuery({ storeData: true });
  const [validateAddressApi] = useValidateAddressMutation();
  const statesList = useAppSelector(selectStates);

  const resetFormState = () => {
    setAddressString('');
    setFullAddress({} as ParsedAddress);
    setSuggestedAddress({} as ParsedAddress);
    setErrors(errorsInitialState);
    setSuggestions([]);
    setShowSuggestions(false);
    setIsSuggestionsLoading(false);
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  useEffect(() => {
    if (open && pageState === 'EDIT') {
      const { street, apartment, city, state, zipCode, latitude, longitude } = initialAddress;
      setFullAddress({ street, apartment, city, state, zipCode, latitude, longitude });
      setSuggestedAddress({ street, apartment, city, state, zipCode, latitude, longitude });
      setAddressString(street);
    } else if (!open) {
      resetFormState();
    }
  }, [open, pageState, initialAddress]);

  useEffect(() => {
    const unsub = whenGooglePlacesReady(() => {
      const clients = createPlacesAutocompleteClients();
      if (!clients) return;
      autoCompleteRef.current = clients.autocomplete;
      placesService.current = clients.places;
      sessionToken.current = clients.sessionToken;
    });

    return () => {
      unsub();
      const input = document.getElementById('autocomplete-address-input');
      if (input && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(input);
      sessionToken.current = null;
    };
  }, []);

  useEffect(() => {
    const refreshPlacesSession = () => {
      const clients = createPlacesAutocompleteClients();
      if (!clients) return;
      autoCompleteRef.current = clients.autocomplete;
      placesService.current = clients.places;
      sessionToken.current = clients.sessionToken;
      suggestionsCache.current = {};
    };

    const handlePlacesSessionOnResume = () => {
      if (document.visibilityState !== 'visible') return;
      refreshPlacesSession();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) refreshPlacesSession();
    };

    document.addEventListener('visibilitychange', handlePlacesSessionOnResume);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handlePlacesSessionOnResume);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const debouncedPredictions = useRef(
    debounce((input, callback) => {
      if (!autoCompleteRef.current) {
        const clients = createPlacesAutocompleteClients();
        if (clients) {
          autoCompleteRef.current = clients.autocomplete;
          placesService.current = clients.places;
          sessionToken.current = clients.sessionToken;
        }
      }

      const options = {
        componentRestrictions: { country: 'us' },
        fields: ['address_components'],
        strictBounds: false,
        types: ['address']
      };

      if (suggestionsCache.current[input]?.length > 0) {
        if (input !== latestAddressQueryRef.current) return;
        callback(suggestionsCache.current[input]);
        return;
      }

      if (input && autoCompleteRef.current) {
        autoCompleteRef.current.getPlacePredictions(
          { input, sessionToken: sessionToken.current, ...options },
          (predictions, status) => {
            if (input !== latestAddressQueryRef.current) return;
            const hasResults =
              status === window.google.maps.places.PlacesServiceStatus.OK &&
              Array.isArray(predictions) &&
              predictions.length > 0;
            if (hasResults) {
              suggestionsCache.current[input] = predictions;
              callback(predictions);
            } else {
              callback([]);
            }
          }
        );
      } else {
        if (input !== latestAddressQueryRef.current) return;
        callback([]);
      }
    }, 600)
  ).current;

  const checkIfValidState = addressState => {
    const isValidState = statesList.some(
      state =>
        state.name.toLowerCase() === addressState.toLowerCase() ||
        state.state_code.toLowerCase() === addressState.toLowerCase()
    );
    return isValidState;
  };

  const getStateCode = state => {
    const { state_code } = statesList.find(
      stateItem =>
        stateItem.name.toLowerCase() === state.trim().toLowerCase() ||
        stateItem.state_code.toLowerCase() === state.trim().toLowerCase()
    );
    return state_code;
  };

  const validateAddress = async () => {
    let isValid = true;
    const newErrors = { ...errorsInitialState };
    setShowSuggestions(false);
    // Validate ZIP code
    if (!fullAddress.zipCode?.trim()) {
      newErrors.zipCode = 'Please enter a valid zip code.';
      isValid = false;
    } else if (!REGEX.zipCode.test(fullAddress.zipCode)) {
      newErrors.zipCode = 'Please enter a valid zip code.';
      isValid = false;
    }

    // Validate street
    if (!fullAddress.street?.trim()) {
      newErrors.street = 'Please enter a valid street name.';
      isValid = false;
    }

    // Validate city
    if (!fullAddress.city?.trim()) {
      newErrors.city = 'Please enter a valid city.';
      isValid = false;
    }

    // Validate state (empty or invalid)
    const isStateValid = fullAddress.state?.trim() !== '' && checkIfValidState(fullAddress.state);
    if (!isStateValid) {
      newErrors.state = 'Please enter a valid state.';
      isValid = false;
    }

    // Call API only when address differs from suggestion and required fields are valid (do not call when city is empty)
    const addressDiffersFromSuggestion =
      fullAddress.state !== suggestedAddress.state || fullAddress.city !== suggestedAddress.city;
    if (addressDiffersFromSuggestion && isStateValid && fullAddress.city?.trim()) {
      setIsSaveLoading(true);
      try {
        await validateAddressApi({
          street: fullAddress.street?.trim(),
          apartment: fullAddress.apartment?.trim(),
          city: fullAddress.city?.trim(),
          state: fullAddress.state?.trim(),
          stateCode: getStateCode(fullAddress.state),
          zipCode: fullAddress.zipCode?.trim(),
          country: fullAddress.country?.trim()
        }).unwrap();
        setIsSaveLoading(false);
      } catch (error) {
        newErrors.city = 'Please enter a valid city.';
        isValid = false;
        setIsSaveLoading(false);
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleAddressChange = (key, value) => {
    // For ZIP code, only update if it matches the format or is empty
    if (key === AddressFields.zipCode && !(REGEX.zipCodeOnChange.test(value) || value === '')) {
      return;
    }

    setFullAddress(prevState => ({ ...prevState, [key]: value }));
    // Clear the error for the field being edited
    setErrors(prevState => ({ ...prevState, [key]: '' }));
  };

  const handleSave = async () => {
    const isValid = await validateAddress();
    if (isValid) {
      const teamAddress: TeamProfileAddress = {
        street: fullAddress.street || '',
        apartment: fullAddress.apartment || '',
        city: fullAddress.city || '',
        state: fullAddress.state || '',
        zipCode: fullAddress.zipCode || '',
        latitude: fullAddress.latitude ?? null,
        longitude: fullAddress.longitude ?? null
      };
      updateAddress(teamAddress);
    }
  };

  const handleInputChange = value => {
    latestAddressQueryRef.current = value;
    setAddressString(value);
    setShowSuggestions(value.length >= 3);
    setErrors(prevState => ({ ...prevState, street: '' })); // Clear street error on typing

    const cachedPrefix = Object.keys(suggestionsCache.current).find(
      key => value.startsWith(key) && suggestionsCache.current[key]?.length > 0
    );

    if (cachedPrefix) {
      const filteredSuggestions = suggestionsCache.current[cachedPrefix].filter(
        suggestion => suggestion.description.toLowerCase().includes(value.toLowerCase())
        // eslint-disable-next-line function-paren-newline
      );

      if (filteredSuggestions.length > 0) {
        setIsSuggestionsLoading(false);
        setSuggestions(filteredSuggestions);
        return;
      }
    }

    if (value.length >= 3) {
      setIsSuggestionsLoading(true);
      debouncedPredictions(value, predictions => {
        setSuggestions(predictions);
        setIsSuggestionsLoading(false);
      });
      return;
    }

    setIsSuggestionsLoading(false);
    setSuggestions([]);
  };

  const handleSuggestionClick = placeSuggestion => {
    const { place_id: placeId, description } = placeSuggestion;
    setAddressString(description);
    setIsSuggestionsLoading(false);

    placesService.current?.getDetails(
      {
        placeId,
        fields: ['address_components', 'geometry'],
        sessionToken: sessionToken.current
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const address: ParsedAddress = {
            ...googleAdressParser(place.address_components),
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
          };

          setFullAddress(address);
          setSuggestedAddress(address);
          setShowSuggestions(false);
          setErrors(errorsInitialState); // Clear all errors when selecting from suggestions
          sessionToken.current = new window.google.maps.places.AutocompleteSessionToken();
        }
      }
    );
  };

  const getButtonText = () => {
    if (isSaveLoading) return 'Validating...';
    return pageState === 'EDIT' ? 'Update' : 'Add';
  };

  return (
    <CustomModal
      open={open}
      onOutsideClick={handleClose}
      width="w-[90%] md:w-[607px]"
      height="md:max-h-[712px]"
      className="customNormalScroll overflow-auto">
      <div className="flex h-full flex-col overflow-auto p-4 md:h-[431px] md:px-8 md:py-6">
        <div className="flex items-center justify-between pb-6">
          <div className="text-xl font-bold text-headingGray">Add business address</div>
          <FormCloseIcon width={26} height={26} className="cursor-pointer" onClick={handleClose} />
        </div>
        <div className="grow space-y-4">
          <div className="relative">
            <div className="mb-2 text-sbase font-bold text-headingGray">
              Street<span className="text-red-500">*</span>
            </div>
            <div className={errors.street ? 'pb-2' : ''}>
              <CustomInput
                value={addressString}
                onChange={handleInputChange}
                id="create-team-address-street"
                placeholder="Enter street address"
              />
              {errors.street && <div className="absolute mt-1 text-xs text-error">{errors.street}</div>}
            </div>
            {showSuggestions && (
              <div className="absolute z-10 mt-0.5 w-full rounded border border-borderGray bg-primary shadow-md">
                {suggestions?.map(suggestion => (
                  <div
                    className="cursor-pointer p-3 hover:bg-slate-100"
                    key={suggestion.place_id}
                    onClick={() => handleSuggestionClick(suggestion)}>
                    {suggestion.description}
                  </div>
                ))}
                {!isSuggestionsLoading && suggestions?.length === 0 && (
                  <div className="p-3 text-center text-sm text-primaryText">No results found</div>
                )}
                {isSuggestionsLoading && (
                  <div className="p-2">
                    <Loader lottieClassName="h-8 w-8" />
                  </div>
                )}
              </div>
            )}
          </div>
          {showExpanded && (
            <>
              <div className="flex items-center gap-4">
                <div className="w-1/2">
                  <div className="mb-2 text-sbase font-semibold text-primaryText">Apt/Suite/Unit</div>
                  <CustomInput
                    id="create-team-address-apartment"
                    value={fullAddress.apartment}
                    onChange={value => handleAddressChange(AddressFields.apartment, value)}
                    placeholder="myapartment"
                  />
                </div>
                <div className="w-1/2">
                  <div className="mb-2 text-sbase font-semibold text-primaryText">City</div>
                  <div>
                    <CustomInput
                      id="create-team-address-city"
                      value={fullAddress.city}
                      onChange={value => handleAddressChange(AddressFields.city, value)}
                      placeholder="Seattle"
                    />
                    {errors.city && <div className="absolute mt-1 text-xs text-error">{errors.city}</div>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-1/2">
                  <div className="mb-2 text-sbase font-semibold text-primaryText">State</div>
                  <div>
                    <CustomInput
                      id="create-team-address-state"
                      value={fullAddress.state}
                      onChange={value => handleAddressChange(AddressFields.state, value)}
                      placeholder="WA"
                    />
                    {errors.state && <div className="absolute mt-1 text-xs text-error">{errors.state}</div>}
                  </div>
                </div>
                <div className="w-1/2">
                  <div className="mb-2 text-sbase font-semibold text-primaryText">Zip code</div>
                  <div>
                    <CustomInput
                      id="create-team-address-zipcode"
                      value={fullAddress.zipCode}
                      onChange={value => handleAddressChange(AddressFields.zipCode, value)}
                      placeholder="98101"
                    />
                    {errors.zipCode && <div className="absolute mt-1 text-xs text-error">{errors.zipCode}</div>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <CustomButton
            id="create-team-address-back-button"
            onClick={handleClose}
            className="h-10 w-[100px] px-8"
            type={ButtonType.SECONDARY}>
            Back
          </CustomButton>
          <CustomButton
            id="create-team-address-add-button"
            onClick={handleSave}
            type={ButtonType.PRIMARY}
            disabled={isSaveLoading}
            className="h-10 w-[100px] px-8">
            {getButtonText()}
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default CreateTeamAddressModal;
